// Badges (canvas E06 e E07): medalha redonda com emoji e borda pelo nível. Bloqueado mostra cadeado e progresso.
// Conquistas lista todos por categoria; o badge ganho abre o card de compartilhar (1080 x 1080, desenhado no
// aparelho) com apelido, badge e link de convite, para o WhatsApp ou para baixar.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  type Badge, type BadgeGanho, CATALOGO_DE_BADGES, type CategoriaBadge, type ConquistaNaTela, type Conquistas, type NivelBadge, type TabelaDeRecompensas,
} from '@truco6/gamificacao';
import { escolherMarca } from '@truco6/ui/escolher-marca';
import { ENDERECO_DA_MARCA } from '@truco6/ui/Marca';
import { dataCompleta, lerConquistas, lerEu } from '../comum/api.ts';
import type { PerfilConta, Sessao } from '../comum/conta.ts';
import { medir, medirUmaVez } from '../comum/medicao.ts';
import { ir, voltar } from '../comum/rotas.ts';
import { Tela, Topo } from '../telas/comum.tsx';
import { Tento } from '../telas/tentos-comum.tsx';

export const NOME_DO_NIVEL: Record<NivelBadge, string> = { bronze: 'Bronze', prata: 'Prata', ouro: 'Ouro', diamante: 'Diamante' };
export const NOME_DA_CATEGORIA: Record<CategoriaBadge, string> = {
  partidas: 'Partidas', vitorias: 'Vitórias', lances: 'Lances', social: 'Social', salas: 'Salas', ofensiva: 'Ofensiva',
  temporada: 'Temporada', divisao: 'Divisão', regional: 'Regional', patrocinado: 'Liga patrocinada', campeonatos: 'Campeonatos',
};
// Borda do nível: bronze, prata e ouro das moedas dos planos; o diamante usa o creme com brilho dourado.
const COR_DO_NIVEL: Record<NivelBadge, string> = { bronze: '--bronze-400', prata: '--prata-300', ouro: '--gold-400', diamante: '--cream-050' };
const numero = (n: number) => n.toLocaleString('pt-BR');

export function Medalha({ b, tamanho = 60, bloqueada = false }: { b: Pick<Badge, 'emoji' | 'nivel'>; tamanho?: number; bloqueada?: boolean }) {
  const borda = bloqueada ? 'var(--line-600)' : `var(${COR_DO_NIVEL[b.nivel]})`;
  return (
    <span className={'medalha' + (bloqueada ? ' bloqueada' : '') + (b.nivel === 'diamante' && !bloqueada ? ' diamante' : '')}
      style={{ width: tamanho, height: tamanho }} aria-hidden="true">
      <svg width={tamanho} height={tamanho} viewBox="0 0 60 60" fill="none">
        <circle cx="30" cy="30" r="28.5" stroke={borda} strokeWidth="3" />
        <circle cx="30" cy="30" r="25" fill={bloqueada ? 'var(--ground-700)' : 'var(--gold-400)'} />
        <circle cx="30" cy="30" r="21.5" stroke={bloqueada ? 'var(--line-600)' : 'var(--gold-600)'} strokeWidth="4" strokeDasharray="5.6 5" />
        <circle cx="30" cy="30" r="16" fill={bloqueada ? 'var(--ground-800)' : 'var(--paper-2)'} stroke={bloqueada ? 'var(--line-600)' : 'var(--gold-600)'} strokeWidth="2" />
      </svg>
      <span className="medalha-emoji" style={{ fontSize: Math.round(tamanho * 0.43) }}>{b.emoji}</span>
      {bloqueada && (
        <span className="medalha-cadeado">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
          </svg>
        </span>
      )}
    </span>
  );
}

export function Progresso({ valor, meta, rotulo, fina = false }: { valor: number; meta: number; rotulo: string; fina?: boolean }) {
  return (
    <span className={'progresso-badge' + (fina ? ' fina' : '')} role="progressbar" aria-label={rotulo} aria-valuemin={0} aria-valuemax={meta} aria-valuenow={valor}>
      <i style={{ width: `${Math.round((valor / meta) * 100)}%` }} />
    </span>
  );
}

// Badges ganhos no fim da partida, com o GA4 badge_ganho uma vez por partida e badge.
export function BadgesNoFim({ ganhos, partida, apelido, lerConvite }: {
  ganhos: BadgeGanho[]; partida: string; apelido: string; lerConvite?: () => Promise<string | null>;
}) {
  const [card, setCard] = useState<Badge | null>(null);
  useEffect(() => {
    for (const g of ganhos) medirUmaVez(`badge:${partida}:${g.badge}`, 'badge_ganho', { badge: g.badge, nivel: g.nivel });
  }, [partida, ganhos.length]);
  if (!ganhos.length) return null;
  return (
    <section className="cartao badges-fim" aria-label="Badges novos">
      <b className="rotulo">{ganhos.length > 1 ? `${ganhos.length} BADGES NOVOS` : 'BADGE NOVO'}</b>
      <ul>
        {ganhos.map((g) => {
          const b = doCatalogo(g);
          return (
            <li key={g.badge}>
              <button onClick={() => setCard(b)} aria-label={`${g.nome}, ${NOME_DO_NIVEL[g.nivel]}. Compartilhar`}>
                <Medalha b={g} tamanho={52} />
                <span><b>{g.nome}</b><small className={'nivel-' + g.nivel}>{NOME_DO_NIVEL[g.nivel]}</small></span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="nota">Toque no badge para compartilhar.</p>
      {card && <CardDeCompartilhar b={card} apelido={apelido} lerConvite={lerConvite} aoFechar={() => setCard(null)} />}
    </section>
  );
}

function doCatalogo(g: BadgeGanho): Badge {
  return CATALOGO_DE_BADGES.find((x) => x.id === g.badge) ?? { id: g.badge, categoria: 'partidas', nome: g.nome, emoji: g.emoji, nivel: g.nivel, contador: '', meta: 1, descricao: '', ordem: 0 };
}

// Galeria do perfil (canvas E07, "Em destaque"): os 4 ganhos mais recentes e o atalho para Conquistas.
export function BadgesDoPerfil({ sessao }: { sessao: Sessao }) {
  const [c, setC] = useState<Conquistas | null>(null);
  useEffect(() => { let vivo = true; void lerConquistas(sessao).then((x) => { if (vivo) setC(x); }, () => {}); return () => { vivo = false; }; }, [sessao.id]);
  if (!c) return null;
  const destaque = c.badges.filter((x) => x.ganho).sort((a, b) => b.ganho!.em - a.ganho!.em || b.ordem - a.ordem).slice(0, 4);
  return (
    <section className="cartao badges-perfil" aria-label="Seus badges">
      <b className="rotulo">EM DESTAQUE</b>
      {destaque.length ? (
        <ul>
          {destaque.map((b) => (
            <li key={b.id}><button onClick={() => ir({ tela: 'conquistas', badge: b.id })}><Medalha b={b} /><span>{b.nome}</span></button></li>
          ))}
        </ul>
      ) : <p className="nota">Termine a primeira partida para ganhar o badge Estreia.</p>}
      <button className="link" onClick={() => ir({ tela: 'conquistas', badge: null })}>Ver conquistas ({c.ganhos} de {c.total})</button>
    </section>
  );
}

const CATEGORIAS = Object.keys(NOME_DA_CATEGORIA) as CategoriaBadge[];

export function ConquistasTela({ sessao, perfil, badge }: { sessao: Sessao; perfil: PerfilConta; badge: string | null }) {
  const [c, setC] = useState<(Conquistas & { recompensas?: TabelaDeRecompensas }) | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [categoria, setCategoria] = useState<CategoriaBadge | null>(null);
  const [aberto, setAberto] = useState<string | null>(badge);
  const [card, setCard] = useState<Badge | null>(null);
  useEffect(() => {
    let vivo = true;
    lerConquistas(sessao).then((x) => { if (vivo) setC(x); }, (e: Error) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [sessao.id]);
  useEffect(() => { setAberto(badge); }, [badge]);
  const lerConvite = useMemo(() => (sessao.anonimo ? undefined : () => lerEu(sessao).then((e) => e.indicacao?.link ?? null)), [sessao.id]);

  const perto = useMemo(() => (c?.badges ?? []).filter((x) => !x.ganho && x.valor > 0)
    .sort((a, b) => b.valor / b.meta - a.valor / a.meta || a.ordem - b.ordem).slice(0, 3), [c]);
  const visiveis = (c?.badges ?? []).filter((x) => !categoria || x.categoria === categoria);
  const presentes = CATEGORIAS.filter((k) => c?.badges.some((x) => x.categoria === k));
  const sel = c?.badges.find((x) => x.id === aberto) ?? null;
  const faltam = c ? c.total - c.ganhos : 0, bemPerto = (c?.badges ?? []).filter((x) => !x.ganho && x.valor / x.meta >= 0.7).length;

  return (
    <Tela larga>
      <Topo titulo="Conquistas" voltar={voltar} />
      <div className="conquistas">
        <div className="conquistas-lado">
          {erro && <p className="erro" role="alert">{erro}</p>}
          {!c && !erro && <p className="carregando" role="status">Um instante...</p>}
          {c && (
            <section className="cartao resumo-conquistas" aria-label="Resumo das conquistas">
              <p><b>{c.ganhos}</b><span>de {c.total}</span></p>
              <div>
                <Progresso valor={c.ganhos} meta={c.total} rotulo={`Conquistas: ${c.ganhos} de ${c.total}`} />
                <small>{faltam ? `Faltam ${faltam}` + (bemPerto ? ` · ${bemPerto} bem perto` : '') : 'Você tem todos!'}</small>
              </div>
            </section>
          )}
          {perto.length > 0 && (
            <>
              <h2 className="rotulo dourado">MAIS PERTO</h2>
              <ul className="cartao mais-perto">
                {perto.map((b) => (
                  <li key={b.id}>
                    <button onClick={() => setAberto(b.id)}>
                      <Medalha b={b} tamanho={40} bloqueada />
                      <span className="mais-perto-texto">
                        <span><b>{b.nome}</b><em>{numero(b.valor)}/{numero(b.meta)}</em></span>
                        <Progresso valor={b.valor} meta={b.meta} rotulo={`${b.nome}: ${numero(b.valor)} de ${numero(b.meta)}`} />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        {c && (
          <div className="conquistas-grade">
            <div className="chips-categoria" role="radiogroup" aria-label="Categoria">
              <button role="radio" aria-checked={!categoria} className={!categoria ? 'on' : undefined} onClick={() => setCategoria(null)}>Todas</button>
              {presentes.map((k) => (
                <button key={k} role="radio" aria-checked={categoria === k} className={categoria === k ? 'on' : undefined} onClick={() => setCategoria(k)}>{NOME_DA_CATEGORIA[k]}</button>
              ))}
            </div>
            <ul className="grade-badges">
              {visiveis.map((b) => <ItemDaGrade key={b.id} b={b} aoAbrir={() => setAberto(b.id)} />)}
            </ul>
            {c.recompensas && <Recompensas t={c.recompensas} />}
          </div>
        )}
      </div>
      {sel && <DetalheDoBadge b={sel} aoFechar={() => setAberto(null)} aoCompartilhar={() => { setCard(sel); setAberto(null); }} />}
      {card && <CardDeCompartilhar b={card} apelido={perfil.apelido} lerConvite={lerConvite} aoFechar={() => setCard(null)} />}
    </Tela>
  );
}

// As recompensas (badges e recompensas, item 18): o que cada ação rende e os limites. Os Termos (15.2) remetem aqui.
function Recompensas({ t }: { t: TabelaDeRecompensas }) {
  const grupos = [...new Set(t.linhas.map((x) => x.grupo))];
  return (
    <section className="cartao recompensas" aria-label="Recompensas">
      <h2 className="rotulo dourado">RECOMPENSAS</h2>
      {grupos.map((gr) => (
        <div key={gr} className="grupo-recompensa">
          <b>{gr}</b>
          <ul>
            {t.linhas.filter((x) => x.grupo === gr).map((x) => (
              <li key={x.acao}>
                <span>{x.acao}{x.extra && <small>{x.extra}</small>}</span>
                <span className="valores">
                  {x.rodagem > 0 && <b>+{numero(x.rodagem)}</b>}
                  {x.tentos > 0 && <b className="tentos"><Tento tamanho={14} />{x.tentos}</b>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <ul className="limites">
        <li>Os números em dourado são Rodagem; o grão é Tento.</li>
        <li>Missões, baús e passe: até {t.tetoMes} Tentos por mês.</li>
        <li>Convites: até {t.convitesPorMes} amigos por mês pagam Tentos.</li>
        <li>Tentos de recompensa só para conta Google. Quem compra não recebe Tentos de recompensa pela própria compra.</li>
      </ul>
    </section>
  );
}

function ItemDaGrade({ b, aoAbrir }: { b: ConquistaNaTela; aoAbrir: () => void }) {
  const rotulo = b.ganho ? `${b.nome}, ${NOME_DO_NIVEL[b.nivel]}` : `${b.nome}, bloqueado, ${numero(b.valor)} de ${numero(b.meta)}`;
  return (
    <li>
      <button className={b.ganho ? undefined : 'bloqueado'} onClick={aoAbrir} aria-label={rotulo}>
        <Medalha b={b} bloqueada={!b.ganho} />
        <span className="nome-badge">{b.nome}</span>
        {b.ganho ? <small className={'nivel-' + b.nivel}>{NOME_DO_NIVEL[b.nivel]}</small> : (
          <>
            <Progresso valor={b.valor} meta={b.meta} rotulo={`${b.nome}: ${numero(b.valor)} de ${numero(b.meta)}`} fina />
            <small>{numero(b.valor)}/{numero(b.meta)}</small>
          </>
        )}
      </button>
    </li>
  );
}

function DetalheDoBadge({ b, aoFechar, aoCompartilhar }: { b: ConquistaNaTela; aoFechar: () => void; aoCompartilhar: () => void }) {
  return (
    <div className="folha-fundo" onClick={aoFechar}>
      <div className="folha detalhe-badge" role="dialog" aria-modal="true" aria-label={b.nome} onClick={(ev) => ev.stopPropagation()}>
        <Medalha b={b} tamanho={120} bloqueada={!b.ganho} />
        <h2>{b.nome}</h2>
        <small className={'etiqueta-nivel nivel-' + b.nivel}>{NOME_DO_NIVEL[b.nivel]} · {NOME_DA_CATEGORIA[b.categoria]}</small>
        <p>{b.descricao}</p>
        {b.ganho ? <p className="nota">Ganho em {dataCompleta(b.ganho.em)}.</p> : (
          <div className="detalhe-progresso">
            <Progresso valor={b.valor} meta={b.meta} rotulo={`${b.nome}: ${numero(b.valor)} de ${numero(b.meta)}`} />
            <p className="nota">{numero(b.valor)} de {numero(b.meta)}</p>
          </div>
        )}
        {b.ganho && <button className="btn1" onClick={aoCompartilhar}>COMPARTILHAR</button>}
        <button className="btn2" onClick={aoFechar}>Fechar</button>
      </div>
    </div>
  );
}

// ---- card de compartilhar ----
const LADO = 1080;

function cor(nome: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(nome).trim() || '#071811';
}

// Desenha o card no aparelho: marca no topo, a medalha no feltro, o nome do badge, o apelido e o link de convite.
export async function desenharCard(cv: HTMLCanvasElement, b: Pick<Badge, 'nome' | 'emoji' | 'nivel' | 'descricao'>, apelido: string, link: string): Promise<void> {
  try { await document.fonts.ready; } catch { /* sem a API de fontes: segue com a fonte de reserva */ }
  cv.width = LADO; cv.height = LADO;
  const g = cv.getContext('2d');
  if (!g) return;
  const display = '"Barlow Condensed", "Arial Narrow", sans-serif', ui = 'Barlow, "Segoe UI", sans-serif';
  g.fillStyle = cor('--ground-900'); g.fillRect(0, 0, LADO, LADO);
  const brilho = g.createRadialGradient(540, 430, 60, 540, 430, 560);
  brilho.addColorStop(0, 'rgba(242,193,78,.22)'); brilho.addColorStop(1, 'rgba(242,193,78,0)');
  g.fillStyle = brilho; g.fillRect(0, 0, LADO, LADO);
  // Marca: o horizontal para fundo escuro, centrado no topo, com o respiro de 25% da altura até a borda.
  const m = escolherMarca('horizontal', 'escuro', 72), logo = new Image();
  logo.src = ENDERECO_DA_MARCA[m.arquivo]!;
  try { await logo.decode(); g.drawImage(logo, (LADO - m.largura) / 2, 40, m.largura, m.altura); } catch { /* sem a imagem, o card sai sem a marca */ }
  g.textAlign = 'center'; g.textBaseline = 'alphabetic';
  // Feltro hexagonal com moldura de madeira, como a mesa
  const hex = (r: number) => {
    g.beginPath();
    for (let i = 0; i < 6; i++) { const a = Math.PI / 3 * i; g.lineTo(540 + r * Math.cos(a), 430 + r * Math.sin(a)); }
    g.closePath();
  };
  hex(300); g.fillStyle = cor('--wood-700'); g.fill();
  hex(286); g.fillStyle = cor('--wood-500'); g.fill();
  hex(262);
  const feltro = g.createRadialGradient(540, 430, 20, 540, 430, 262);
  feltro.addColorStop(0, cor('--felt-500')); feltro.addColorStop(1, cor('--felt-700'));
  g.fillStyle = feltro; g.fill();
  // Medalha
  const anel = cor(COR_DO_NIVEL[b.nivel]);
  if (b.nivel === 'diamante') { g.shadowColor = cor('--gold-400'); g.shadowBlur = 40; }
  g.beginPath(); g.arc(540, 430, 196, 0, Math.PI * 2); g.lineWidth = 20; g.strokeStyle = anel; g.stroke();
  g.shadowBlur = 0;
  g.beginPath(); g.arc(540, 430, 176, 0, Math.PI * 2); g.fillStyle = cor('--gold-400'); g.fill();
  g.setLineDash([38, 34]); g.beginPath(); g.arc(540, 430, 150, 0, Math.PI * 2); g.lineWidth = 26; g.strokeStyle = cor('--gold-600'); g.stroke(); g.setLineDash([]);
  g.beginPath(); g.arc(540, 430, 112, 0, Math.PI * 2); g.fillStyle = cor('--paper-2'); g.fill(); g.lineWidth = 12; g.strokeStyle = cor('--gold-600'); g.stroke();
  g.textBaseline = 'middle'; g.font = `150px ${ui}`; g.fillStyle = cor('--ink-900'); g.fillText(b.emoji, 540, 440);
  g.textBaseline = 'alphabetic';
  // Nível, nome e descrição
  g.font = `800 34px ${ui}`; g.fillStyle = anel; g.letterSpacing = '6px';
  g.fillText(NOME_DO_NIVEL[b.nivel].toUpperCase(), 540, 788);
  g.letterSpacing = '0px';
  g.font = `800 92px ${display}`; g.fillStyle = cor('--ink-050'); ajustar(g, b.nome, 960, 92, display); g.fillText(b.nome, 540, 872);
  g.font = `600 40px ${ui}`; g.fillStyle = cor('--gold-400'); ajustar(g, `Conquista de ${apelido}`, 960, 40, ui, 600); g.fillText(`Conquista de ${apelido}`, 540, 930);
  // Faixa do convite
  g.fillStyle = cor('--ground-800'); g.fillRect(0, 970, LADO, 110);
  g.fillStyle = cor('--line-600'); g.fillRect(0, 970, LADO, 2);
  g.font = `600 30px ${ui}`; g.fillStyle = cor('--ink-300'); g.fillText('Vem jogar comigo', 540, 1012);
  g.font = `800 38px ${ui}`; g.fillStyle = cor('--ink-050'); const curto = link.replace(/^https?:\/\//, ''); ajustar(g, curto, 1000, 38, ui, 800); g.fillText(curto, 540, 1058);
}

function ajustar(g: CanvasRenderingContext2D, texto: string, largura: number, tamanho: number, familia: string, peso = 800): void {
  let t = tamanho;
  g.font = `${peso} ${t}px ${familia}`;
  while (t > 18 && g.measureText(texto).width > largura) { t -= 2; g.font = `${peso} ${t}px ${familia}`; }
}

export function CardDeCompartilhar({ b, apelido, lerConvite, aoFechar }: {
  b: Badge; apelido: string; lerConvite?: () => Promise<string | null>; aoFechar: () => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    (async () => {
      const convite = (await lerConvite?.().catch(() => null)) ?? location.origin;
      const cv = ref.current;
      if (!vivo || !cv) return;
      await desenharCard(cv, b, apelido, convite);
      if (!vivo) return;
      setLink(convite);
      setUrl(cv.toDataURL('image/png'));
    })();
    return () => { vivo = false; };
  }, [b.id]);
  const texto = `Ganhei o badge ${b.nome} no Truco de 6. Vem jogar comigo: ${link ?? location.origin}`;

  async function whatsapp() {
    medir('share', { method: 'whatsapp', content_type: 'badge', item_id: b.id });
    const cv = ref.current;
    const blob = cv ? await new Promise<Blob | null>((ok) => cv.toBlob(ok, 'image/png')) : null;
    const arquivo = blob ? new File([blob], `truco6-${b.id}.png`, { type: 'image/png' }) : null;
    if (arquivo && navigator.canShare?.({ files: [arquivo] })) {
      try { await navigator.share({ files: [arquivo], text: texto }); return; } catch (e) { if ((e as Error).name === 'AbortError') return; }
    }
    window.open('https://wa.me/?text=' + encodeURIComponent(texto), '_blank', 'noopener');
    setAviso('No computador, baixe a imagem e anexe na conversa.');
  }

  // Num portal: na mesa, o palco tem escala própria e prenderia a tela inteira dentro dele.
  return createPortal(
    <div className="card-compartilhar" role="dialog" aria-modal="true" aria-label={`Compartilhar o badge ${b.nome}`}>
      <div className="card-compartilhar-topo">
        <button className="voltar" aria-label="Fechar" onClick={aoFechar}>‹</button>
        <h1>Compartilhar badge</h1>
      </div>
      <canvas ref={ref} hidden />
      <div className="card-compartilhar-corpo">
        <figure className="card-previa">
          {url ? <img src={url} alt={`Card do badge ${b.nome}, de ${apelido}, com o link de convite`} /> : <span role="status">Montando o card...</span>}
        </figure>
        <div className="card-acoes">
          <p className="nota">Imagem 1080 x 1080 com o seu apelido, o badge e o seu link de convite.</p>
          <button className="btn1 whatsapp" disabled={!url} onClick={() => void whatsapp()}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3.5 20.5l1.4-4.2A8.5 8.5 0 1 1 8 19.3l-4.5 1.2z" /><path d="M9 9.5c0 3 2.5 5.5 5.5 5.5l1-1.5-2-1-1 .8a4 4 0 0 1-1.8-1.8l.8-1-1-2L9 9.5z" />
            </svg>
            MANDAR NO WHATSAPP
          </button>
          {url && (
            <a className="btn2" href={url} download={`truco6-${b.id}.png`} onClick={() => medir('share', { method: 'download', content_type: 'badge', item_id: b.id })}>Baixar imagem</a>
          )}
          {aviso && <p className="nota" role="status">{aviso}</p>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
